import { globalEnv } from "~/global-env";
import { toDbId } from "~/utils/graphql-helpers";
import { PositionNode, PositionRowData } from "./types";

const UNASSIGNED_ACCOUNT = "Unassigned";

/**
 * `complianceProgram` is typed as a scalar string in older versions of the
 * schema but the gateway returns an array. Normalize both shapes and drop any
 * empty values so callers always get a clean list of program labels.
 */
export const normalizePrograms = (
  complianceProgram: PositionNode["complianceProgram"] | string,
): string[] => {
  const values = Array.isArray(complianceProgram) ? complianceProgram : [complianceProgram];
  return values.filter((program): program is string => Boolean(program));
};

/** Rows for a single compliance profile: every position carries that profile's name. */
export const profileRows = (
  profileName: string,
  positions: PositionNode[],
): PositionRowData[] =>
  positions.map((position) => ({
    id: position.id,
    profileName,
    accountName: position.investmentEntity?.name ?? UNASSIGNED_ACCOUNT,
    programs: normalizePrograms(position.complianceProgram),
  }));

/** Positions page filtered to a compliance profile's positions. */
export const buildViewAllPositionsHref = (positions: PositionNode[]): string => {
  const positionDbIds = [...new Set(positions.map((position) => toDbId(position.id)))];
  const arenaDbIds = [
    ...new Set(
      positions
        .map((position) => position.arena?.id)
        .filter((id): id is string => Boolean(id))
        .map(toDbId),
    ),
  ];

  const base = `/canopy${globalEnv.complianceRoute}/positions`;
  const params = new URLSearchParams();
  if (arenaDbIds.length > 0) params.set("arenaIds", arenaDbIds.join(","));
  if (positionDbIds.length > 0) params.set("positionIds", positionDbIds.join(","));

  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};
